import { useEffect } from "react";
import { guideOpen, type GuideBlock, type GuideSection } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { Banner, SectionBar, Skeleton, Table } from "@/ui";

// V1-INTERACTIONS §4.25 — the Talent Acquisition Process Guide, read-only, inside the application (EM-4 / AF-4).
// The guide is published CONFIGURATION: every word on this screen comes from `guide.open`, nothing is written here.
// The document's own grammar is kept — numbered sections, numbered tiles, its tables and its rule callouts — so a
// reader who knows the PDF recognises the screen (Eduardo's numbered-tile grammar, CALL-16 15:46 "I like the format").

export function Guide() {
  const { register } = useViewState();
  const q = useRead(["guide"], () => guideOpen({}));
  const guide = q.data;
  useEffect(
    () => register({ view: "guide", ids: {}, summary: `process guide v${guide?.version ?? "?"} · ${guide?.sections?.length ?? 0} sections` }),
    [register, guide?.version, guide?.sections?.length],
  );
  const sections = guide?.sections ?? [];
  return (
    <div className="doc">
      <h1 className="page">{guide?.title ?? "Talent Acquisition Process Guide"}</h1>
      <p className="lede">{guide?.subtitle ?? "The published recruiting process, end to end."}</p>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton rows={6} />}
      {guide && (
        <>
          <dl className="kv doc__meta">
            <div><dt>Prepared by</dt><dd>{guide.prepared_by}</dd></div>
            <div><dt>Audience</dt><dd>{guide.audience}</dd></div>
            <div><dt>Classification</dt><dd>{guide.classification}</dd></div>
            <div><dt>Published version</dt><dd>v{guide.version}</dd></div>
          </dl>
          {!!guide.highlights?.length && (
            <div className="grid grid--kpi">
              {guide.highlights.map(h => (
                <div className="card kpi" key={h.label}>
                  <b>{h.number}</b>
                  <span>{h.label}</span>
                </div>
              ))}
            </div>
          )}
          {/* The guide's own "click any section to jump" contents page. Buttons, not in-page anchors: the hash
              belongs to the router (§1.3), so a jump moves focus and scroll rather than the route. */}
          <SectionBar>
            {sections.map(section => (
              <button key={section.number} type="button" aria-controls={`guide-${section.number}`} onClick={() => focusSection(`guide-${section.number}`)}>
                <span className="mono">{section.number}</span> {section.title}
              </button>
            ))}
          </SectionBar>
          {sections.map(section => <Section key={section.number} section={section} />)}
          <p className="muted doc__footer">{guide.footer}{guide.tagline ? ` · ${guide.tagline}` : ""}</p>
        </>
      )}
    </div>
  );
}

export function focusSection(id: string) {
  const section = document.getElementById(id);
  section?.focus({ preventScroll: true });
  section?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
}

function Section({ section }: { section: GuideSection }) {
  return (
    <section className="card doc__section" id={`guide-${section.number}`} tabIndex={-1} aria-labelledby={`guide-h-${section.number}`}>
      <h2 className="sec" id={`guide-h-${section.number}`}>
        <span className="doc__n">{section.number}</span> {section.title}
      </h2>
      {section.summary && <p className="muted">{section.summary}</p>}
      {(section.blocks ?? []).map((block, index) => <Block key={`${section.number}-${index}`} block={block} label={`${section.title} — ${block.heading ?? "detail"}`} />)}
    </section>
  );
}

function Block({ block, label }: { block: GuideBlock; label: string }) {
  if (block.kind === "table") {
    const columns = block.columns ?? [];
    return (
      <>
        {block.heading && <h3 className="sub">{block.heading}</h3>}
        {block.note && <p className="muted">{block.note}</p>}
        <Table label={label} mobile="stack" head={<tr>{columns.map(column => <th key={column}>{column}</th>)}</tr>}>
          {(block.rows ?? []).map(row => (
            <tr key={row.join("|")}>
              {row.map((cell, index) => <td key={index} className={index === 0 ? "sticky-l" : undefined}>{cell}</td>)}
            </tr>
          ))}
        </Table>
      </>
    );
  }
  if (block.kind === "tiles") {
    return (
      <>
        {block.heading && <h3 className="sub">{block.heading}</h3>}
        <ol className="tiles">
          {(block.items ?? []).map(tile => (
            <li className={tile.number ? "tile" : "tile tile--plain"} key={`${tile.number ?? ""}${tile.title}`}>
              {tile.number && <span className="tile__n" aria-hidden="true">{tile.number}</span>}
              <div>
                {tile.tag && <span className="tile__tag">{tile.tag}</span>}
                <b className="tile__title">{tile.number ? <span className="sr-only">{tile.number}. </span> : null}{tile.title}</b>
                {tile.text && <p>{tile.text}</p>}
              </div>
            </li>
          ))}
        </ol>
      </>
    );
  }
  if (block.kind === "callout") {
    return (
      <div className="callout doc__rule">
        {block.heading && <h3 className="sub">{block.heading}</h3>}
        {(block.paragraphs ?? []).map(text => <p key={text}>{text}</p>)}
      </div>
    );
  }
  return (
    <>
      {block.heading && <h3 className="sub">{block.heading}</h3>}
      {(block.paragraphs ?? []).map(text => <p key={text}>{text}</p>)}
    </>
  );
}
